<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDialogs, _resolveActive } from '@/composables/useDialogs';
import BaseModal from './BaseModal.vue';

/**
 * Single root mount-point for the app's confirm/alert/prompt dialogs.
 * Watches the singleton state in useDialogs() and renders whichever
 * kind of dialog is currently active.
 *
 * Mount once, in App.vue.
 */

const { active } = useDialogs();

const isOpen = computed(() => active.value != null);

// Prompt input — bound by the prompt panel.
const promptValue = ref('');

// Reset the input every time a new prompt opens.
watch(active, (cur) => {
    if (cur?.kind === 'prompt') {
        promptValue.value = (cur.opts as any).initial || '';
    }
}, { immediate: true });

const promptValid = computed(() => {
    const cur = active.value;
    if (cur?.kind !== 'prompt') return true;
    const need = (cur.opts as any).requireMatch as string | undefined;
    if (!need) return true;
    return promptValue.value === need;
});

function onConfirm() { _resolveActive(true); }
function onCancel()  { _resolveActive(false); }
function onOk()      { _resolveActive(undefined); }
function onPromptOk() {
    if (!promptValid.value) return;
    _resolveActive(promptValue.value);
}
function onPromptCancel() { _resolveActive(null); }

function close() {
    const cur = active.value;
    if (!cur) return;
    if (cur.kind === 'confirm') onCancel();
    else if (cur.kind === 'prompt') onPromptCancel();
    else onOk();
}

// Resolve title/tone defaults per-kind so templates stay tidy.
const dialogTitle = computed(() => {
    const cur = active.value;
    if (!cur) return '';
    if (cur.opts.title) return cur.opts.title;
    if (cur.kind === 'confirm') return 'Confirm';
    if (cur.kind === 'alert')   return 'Notice';
    return 'Input required';
});

const confirmBtnClass = computed(() => {
    const cur = active.value;
    const tone = cur && (cur.opts as any).tone;
    if (tone === 'danger') {
        return 'bg-rose-600 hover:bg-rose-700 text-white';
    }
    if (tone === 'warning') {
        return 'bg-amber-500 hover:bg-amber-600 text-white';
    }
    return 'bg-frvb-accent hover:bg-frvb-accent-2 text-white';
});

const headerToneClass = computed(() => {
    const cur = active.value;
    const tone = cur && (cur.opts as any).tone;
    if (tone === 'danger')  return 'text-rose-700';
    if (tone === 'warning') return 'text-amber-700';
    return '';
});
</script>

<template>
    <BaseModal :open="isOpen" @close="close">
        <template #header>
            <span :class="headerToneClass">{{ dialogTitle }}</span>
        </template>

        <!-- Message body — preserve newlines from the message string. -->
        <p v-if="active" class="text-sm text-frvb-ink whitespace-pre-line">{{ active.opts.message }}</p>

        <!-- Prompt input -->
        <div v-if="active?.kind === 'prompt'" class="mt-3">
            <input v-model="promptValue"
                   type="text"
                   :placeholder="(active.opts as any).placeholder || ''"
                   class="w-full border border-frvb-border-s rounded px-3 py-2 text-sm bg-white
                          focus:outline-none focus:border-frvb-accent focus:ring-1 focus:ring-frvb-accent/40"
                   @keydown.enter.prevent="onPromptOk" />
            <p v-if="(active.opts as any).requireMatch && !promptValid"
               class="text-[11px] text-rose-700 mt-1">
                Type
                <code class="font-mono font-bold bg-rose-50 px-1 rounded">{{ (active.opts as any).requireMatch }}</code>
                exactly to enable confirm.
            </p>
        </div>

        <template #footer>
            <!-- ALERT — single OK button -->
            <template v-if="active?.kind === 'alert'">
                <button @click="onOk"
                        class="px-4 py-2 rounded-md text-sm font-semibold transition"
                        :class="confirmBtnClass">
                    {{ (active.opts as any).confirmText || 'OK' }}
                </button>
            </template>

            <!-- CONFIRM — Cancel + action -->
            <template v-else-if="active?.kind === 'confirm'">
                <button @click="onCancel"
                        class="px-4 py-2 rounded-md text-sm font-semibold bg-white border border-frvb-border-s text-frvb-ink hover:bg-frvb-bg-alt transition">
                    {{ (active.opts as any).cancelText || 'Cancel' }}
                </button>
                <button @click="onConfirm"
                        class="px-4 py-2 rounded-md text-sm font-semibold transition"
                        :class="confirmBtnClass">
                    {{ (active.opts as any).confirmText || 'OK' }}
                </button>
            </template>

            <!-- PROMPT — Cancel + Confirm (gated on requireMatch) -->
            <template v-else-if="active?.kind === 'prompt'">
                <button @click="onPromptCancel"
                        class="px-4 py-2 rounded-md text-sm font-semibold bg-white border border-frvb-border-s text-frvb-ink hover:bg-frvb-bg-alt transition">
                    {{ (active.opts as any).cancelText || 'Cancel' }}
                </button>
                <button @click="onPromptOk"
                        :disabled="!promptValid"
                        class="px-4 py-2 rounded-md text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                        :class="confirmBtnClass">
                    {{ (active.opts as any).confirmText || 'OK' }}
                </button>
            </template>
        </template>
    </BaseModal>
</template>
